import React, { useState, useEffect } from 'react';
import { Outlet } from 'react-router-dom';
import { styled, useTheme } from '@mui/material/styles';
import { Box, useMediaQuery } from '@mui/material';
import Header from './Header';
import SideBarContent from '../../views/Stores/Insight/sideBar';

// Dynamic main content that adjusts to sidebar width
const Main = styled((props) => <main {...props} />)(({ theme, sidebarWidth, isSmallLaptop, isMediumLaptop }) => ({
  width: `calc(100% - ${sidebarWidth}px)`,
  minHeight: '100vh',
  flexGrow: 1,
  padding: isSmallLaptop ? theme.spacing(2) : isMediumLaptop ? theme.spacing(2.5) : theme.spacing(3),
  marginLeft: `${sidebarWidth}px`,
  transition: theme.transitions.create(['margin', 'width'], {
    easing: theme.transitions.easing.easeInOut,
    duration: theme.transitions.duration.standard
  }),
  [theme.breakpoints.down('md')]: {
    width: '100%',
    marginLeft: 0,
    padding: theme.spacing(2)
  },
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(1.5)
  }
}));

const OutletDiv = styled((props) => <div {...props} />)(({ theme, isSmallLaptop, isMediumLaptop }) => ({
  padding: isSmallLaptop ? theme.spacing(2.5) : isMediumLaptop ? theme.spacing(3.5) : theme.spacing(4),
  fontFamily: 'Plus Jakarta Sans, sans-serif',
  [theme.breakpoints.down('md')]: { 
    padding: theme.spacing(2) 
  },
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(1.5)
  }
}));

const MainLayout = () => {
  const theme = useTheme();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [sidebarWidth, setSidebarWidth] = useState(72); // Default collapsed width
  const [screenSize, setScreenSize] = useState({
    width: window.innerWidth,
    height: window.innerHeight
  });
  
  // Responsive breakpoints
  const isSmallLaptop = screenSize.width <= 1366;
  const isMediumLaptop = screenSize.width > 1366 && screenSize.width <= 1600;
  // const isLargeLaptop = screenSize.width > 1600;
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  // Handle window resize
  useEffect(() => {
    const handleResize = () => {
      setScreenSize({
        width: window.innerWidth,
        height: window.innerHeight
      });
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  
  // Calculate sidebar width based on drawer state and screen size
  useEffect(() => {
    let collapsedWidth = 72;
    let expandedWidth = 360;
    
    if (isSmallLaptop) {
      collapsedWidth = 64;
      expandedWidth = 320;
    } else if (isMediumLaptop) {
      collapsedWidth = 68;
      expandedWidth = 340;
    }
    
    const width = drawerOpen ? expandedWidth : collapsedWidth;
    setSidebarWidth(width);
  }, [drawerOpen, isSmallLaptop, isMediumLaptop]);

  const handleDrawerToggle = () => setDrawerOpen((v) => !v);

  return (
    <Box sx={{ display: 'flex', width: '100%' }}>
      {/* Header with dynamic positioning */}
      <Header 
        drawerOpen={drawerOpen} 
        drawerToggle={handleDrawerToggle} 
        sidebarWidth={sidebarWidth}
      />
      
      {/* Sidebar */}
      <SideBarContent 
        isExpanded={drawerOpen} 
        isMobile={false} 
        onClose={handleDrawerToggle} 
      />
      
      {/* Main content with dynamic width adjustment */}
      <Main 
        sidebarWidth={isMobile ? 0 : sidebarWidth} 
        isSmallLaptop={isSmallLaptop}
        isMediumLaptop={isMediumLaptop}
      >
        {/* Spacer for fixed header - responsive height */}
        <Box sx={{ 
          height: isSmallLaptop ? 60 : isMediumLaptop ? 64 : 68,
          [theme.breakpoints.down('md')]: { height: 56 },
          [theme.breakpoints.down('sm')]: { height: 52 }
        }} />
        <OutletDiv isSmallLaptop={isSmallLaptop} isMediumLaptop={isMediumLaptop}>
          <Outlet />
        </OutletDiv>
      </Main>
    </Box>
  );
};

export default MainLayout;
